import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('camitexCart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const [ivaData, setIvaData] = useState({}); // Add IVA data state

  // Load cart from localStorage on initial render
  useEffect(() => {
    const storedCart = localStorage.getItem('cart');
    if (storedCart) {
      setCart(JSON.parse(storedCart));
    }

    // Load IVA data from localStorage if available
    const storedIvaData = localStorage.getItem('ivaData');
    if (storedIvaData) {
      setIvaData(JSON.parse(storedIvaData));
    }
  }, []);

  // Update localStorage and recalculate counts whenever cart changes
  useEffect(() => {
    // Calculate total items
    const count = cart.reduce((total, item) => total + item.quantity, 0);
    setCartCount(count);

    // Calculate total price with quantity-based pricing
    const total = cart.reduce((sum, item) => {
      let price;
      if (item.quantity >= 24 && item.precio3) {
        price = parseFloat(item.precio3 || 0);
      } else if (item.quantity >= 12 && item.precio2) {
        price = parseFloat(item.precio2 || 0);
      } else {
        price = parseFloat(item.precio1 || 0);
      }
      return sum + (price * item.quantity);
    }, 0);
    setCartTotal(total);

    // Save to localStorage
    localStorage.setItem('camitexCart', JSON.stringify(cart));
  }, [cart]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  // Save IVA data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('ivaData', JSON.stringify(ivaData));
  }, [ivaData]);

  // Add item to cart
  const addToCart = (item) => {
    // First ensure quantity is a number and at least 1
    const quantity = parseInt(item.quantity);
    if (isNaN(quantity) || quantity < 1) {
      item.quantity = 1; // Default to 1 if invalid
    } else {
      item.quantity = quantity; // Use the passed quantity
    }

    // Check if the item is already in the cart - now using color_numeric_id
    const existingItemIndex = cart.findIndex(
      cartItem =>
        cartItem.id === item.id &&
        cartItem.color_numeric_id === item.color_numeric_id &&
        cartItem.talla_id === item.talla_id
    );

    if (existingItemIndex >= 0) {
      // Item already exists, update quantity instead of adding a new item
      const updatedCart = [...cart];
      updatedCart[existingItemIndex].quantity += item.quantity;
      setCart(updatedCart);
    } else {
      // Item doesn't exist, add it to the cart
      setCart([...cart, item]);
    }

    // Save to localStorage
    localStorage.setItem('cart', JSON.stringify([...cart, item]));
  };

  // Update item quantity
  const updateQuantity = (itemIndex, newQuantity) => {
    if (newQuantity < 1) return;

    setCart(prevCart => {
      const updatedCart = [...prevCart];
      updatedCart[itemIndex].quantity = newQuantity;
      return updatedCart;
    });
  };

  // Remove item from cart
  const removeFromCart = (itemIndex) => {
    setCart(prevCart => prevCart.filter((_, index) => index !== itemIndex));
  };

  // Clear entire cart
  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('camitexCart');
  };

  // Format price for display
  const formatPrice = (price) => {
    // Ensure price is a number
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;

    // Check if it's a valid number before formatting
    if (isNaN(numPrice)) {
      console.error('Invalid price value:', price);
      return '₡0.00'; // Return a default value
    }

    // Format the price with the Costa Rican Colón symbol
    return `₡${numPrice.toFixed(2)}`;
  };

  // Add method to update IVA data
  const updateIvaData = (newIvaData) => {
    setIvaData(prevData => ({...prevData, ...newIvaData}));
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        cartCount,
        cartTotal,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        formatPrice, // Provide formatPrice function to context
        ivaData,       // Expose IVA data
        updateIvaData  // Expose method to update IVA data
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}